import React, { useEffect } from 'react';
import { collectAttestationLinks } from '../utils/attestationLinkUtils';
import { useForm } from 'react-hook-form';
import {
    DocumentSchema,
    SchemaNode,
    ActionNode,
    DocumentFunctionRegistry,
} from '../types';
import { useDocument } from '../DocumentContext';
import type { VariableOption } from './inputs/mentionSuggestion';
import {
    isRequired,
    createCheckDisable,
    createCheckHidden,
    validateRequiredFields,
} from '../utils/formUtils';
import { createEmptyDocumentFunctionRegistry } from '../utils/functionRegistry';
import { DocumentRendererContext } from '../renderer/types';
import { DocumentViewRenderer } from '../renderer/DocumentViewRenderer';
import { DocumentActionRenderer } from '../renderer/DocumentActionRenderer';
import { DocumentInputRenderer } from '../renderer/DocumentInputRenderer';
import { DocumentSectionRenderer } from '../renderer/DocumentSectionRenderer';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { cn } from '../../../lib/utils';

interface DocumentFormProps {
    schema: DocumentSchema;
    dataSources?: Record<string, any[]>;
    externalContextData?: Record<string, any>;
    onSubmit?: (data: any) => void;
    className?: string;
    functions?: Partial<DocumentFunctionRegistry>;
    variableOptions?: VariableOption[];
    generatePreviewText?: (schema: DocumentSchema, data: any) => string;
    PreviewComponent?: React.ComponentType<{
        schema: DocumentSchema;
        data: any;
        dataSources?: any;
    }>;
    visibleSectionIds?: string[];
}

export const DocumentForm: React.FC<DocumentFormProps> = ({
    schema,
    dataSources = {},
    externalContextData = {},
    onSubmit,
    className,
    functions,
    variableOptions,
    generatePreviewText,
    PreviewComponent,
    visibleSectionIds,
}) => {
    const { control, handleSubmit, watch, setValue, reset } = useForm();
    const { updateData, save, complete, data, documentId } = useDocument();
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const hasInitializedFormRef = React.useRef(false);
    const [postActionDialog, setPostActionDialog] = React.useState<{
        open: boolean;
        config: ActionNode['postAction'];
        isSuccess: boolean;
        errorMessage?: string;
    }>({ open: false, config: undefined, isSuccess: true });

    const isEditMode = !!documentId;

    useEffect(() => {
        if (
            hasInitializedFormRef.current ||
            !data ||
            Object.keys(data).length === 0
        ) {
            return;
        }

        reset(data);
        hasInitializedFormRef.current = true;
    }, [data, reset]);

    const handleCopyNote = async () => {
        if (!generatePreviewText) return;
        const text = generatePreviewText(schema, data);
        await navigator.clipboard.writeText(text);
    };

    const handleSave = async () => {
        await save();
    };

    const handleComplete = async (postAction?: ActionNode['postAction']) => {
        await handleSubmit(async () => {
            const result = await complete();
            if (postAction) {
                setPostActionDialog({
                    open: true,
                    config: postAction,
                    isSuccess: result.success,
                    errorMessage: result.error,
                });
            }
        })();
    };

    const resolvedContextData = React.useMemo(
        () => ({
            ...data,
            ...externalContextData,
        }),
        [data, externalContextData]
    );
    const checkDisable = createCheckDisable(watch, resolvedContextData);
    const checkHidden = createCheckHidden(watch, resolvedContextData);

    const validateForComplete = () => {
        return validateRequiredFields(schema, data, checkHidden);
    };

    const baseFunctions = React.useMemo(
        () => createEmptyDocumentFunctionRegistry(),
        []
    );
    const mergedFunctions = React.useMemo(
        () => ({
            ...baseFunctions,
            ...(functions || {}),
            actions: {
                ...baseFunctions.actions,
                ...(functions?.actions || {}),
            },
            inputHandlers: {
                ...baseFunctions.inputHandlers,
                ...(functions?.inputHandlers || {}),
            },
            customInputs: {
                ...(baseFunctions.customInputs || {}),
                ...(functions?.customInputs || {}),
            },
            repeaterHandlers: {
                ...baseFunctions.repeaterHandlers,
                ...(functions?.repeaterHandlers || {}),
            },
            viewHandlers: {
                ...baseFunctions.viewHandlers,
                ...(functions?.viewHandlers || {}),
            },
            backgrounds: {
                ...baseFunctions.backgrounds,
                ...(functions?.backgrounds || {}),
            },
            richTextExtensions: {
                ...(baseFunctions.richTextExtensions || {}),
                ...(functions?.richTextExtensions || {}),
            },
            searchProviders: {
                ...(baseFunctions.searchProviders || {}),
                ...(functions?.searchProviders || {}),
            },
        }),
        [functions, baseFunctions]
    );

    const attestationLinks = React.useMemo(
        () => collectAttestationLinks(schema),
        [schema]
    );

    const context: DocumentRendererContext = {
        control,
        watch,
        setValue,
        updateData,
        dataSources,
        checkDisable,
        checkHidden,
        isRequired,
        contextData: resolvedContextData,
        isEditMode,
        functions: mergedFunctions,
        variableOptions,
        attestationLinks,
    };

    const schemaSurfaceClassName = cn(
        'space-y-8 rounded-xl p-5 md:p-6 transition-colors',
        schema.style?.noBorder
            ? 'border border-transparent shadow-none'
            : 'border border-border/30 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]',
        schema.style?.noBackground ? 'bg-transparent' : 'bg-card/45',
        schema.style?.className
    );
    const schemaSections = Array.isArray(schema.sections) ? schema.sections : [];
    const filteredSections =
        visibleSectionIds && visibleSectionIds.length > 0
            ? schemaSections.filter((section) =>
                  visibleSectionIds.includes(section.id)
              )
            : schemaSections;

    const renderNode = (node: SchemaNode): React.ReactNode => {
        if (node.kind === 'section') {
            return (
                <DocumentSectionRenderer
                    key={node.id}
                    section={node}
                    isNested={true}
                    context={context}
                    renderNode={renderNode}
                />
            );
        }
        if (node.kind === 'view') {
            return (
                <DocumentViewRenderer
                    key={node.id}
                    field={node}
                    watch={watch}
                    contextData={resolvedContextData}
                    updateData={updateData}
                    functions={mergedFunctions}
                />
            );
        }
        if (node.kind === 'input') {
            return (
                <DocumentInputRenderer
                    key={node.id}
                    field={node}
                    context={context}
                />
            );
        }
        if (node.kind === 'action') {
            return (
                <DocumentActionRenderer
                    key={node.id}
                    field={node}
                    context={context}
                    onSuccess={(config) =>
                        setPostActionDialog({
                            open: true,
                            config,
                            isSuccess: true,
                        })
                    }
                    handleCopyNote={handleCopyNote}
                    handlePreview={() => setPreviewOpen(true)}
                    handleSave={handleSave}
                    handleComplete={handleComplete}
                    postActionDialog={postActionDialog}
                    setPostActionDialog={setPostActionDialog}
                    validateForComplete={validateForComplete}
                />
            );
        }
        return null;
    };

    return (
        <form
            onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}
            className={className}
        >
            {(() => {
                // Consecutive sections sharing a `group` render encapsulated
                // in one wrapper card (flat sections with dividers) instead
                // of separate bordered cards.
                interface SectionChunk {
                    group?: string;
                    sections: typeof filteredSections;
                }
                const chunks: SectionChunk[] = [];
                for (const section of filteredSections) {
                    const last = chunks[chunks.length - 1];
                    if (section.group && last && last.group === section.group) {
                        last.sections.push(section);
                    } else {
                        chunks.push({
                            group: section.group,
                            sections: [section],
                        });
                    }
                }

                return chunks.map((chunk) =>
                    chunk.group ? (
                        <div
                            key={`group-${chunk.group}-${chunk.sections[0]?.id}`}
                            className="overflow-hidden rounded-xl border border-border/45 shadow-sm divide-y divide-border/35"
                        >
                            {chunk.sections.map((section) => (
                                <DocumentSectionRenderer
                                    key={section.id}
                                    section={section}
                                    isNested={false}
                                    variant="grouped"
                                    context={context}
                                    renderNode={renderNode}
                                />
                            ))}
                        </div>
                    ) : (
                        chunk.sections.map((section) => (
                            <DocumentSectionRenderer
                                key={section.id}
                                section={section}
                                isNested={false}
                                context={context}
                                renderNode={renderNode}
                            />
                        ))
                    )
                );
            })()}
        </form>
    );
};
